import { SectionComponent } from '../../components/section/section.component';
import { AfterViewInit, ChangeDetectionStrategy, Component, DestroyRef, ElementRef, OnInit, PLATFORM_ID, QueryList, ViewChildren, inject } from '@angular/core';
import { PlainImageComponent, PlainImageSection, isPlainImageSection } from '../../../app/components/plain-image/plain-image.component';
import { InteractiveImageComponent, InteractiveImageSection, isInteractiveImageSection } from '../interactive-image/interactive-image.component';
import { AnySection, InnerSection, InnerSectionAttributes, Section, SectionAttributes, NumColumns } from '../../../app/types/section';
import { Observable, map, merge, mergeMap, of, startWith } from 'rxjs';
import { ActivatedRoute, Router } from '@angular/router';
import { preventScroll } from '../../../app/providers/page.service';
import { CommonModule, isPlatformBrowser } from '@angular/common';
import { EditorIdDirective } from '../../../app/directives/editor-id.directive';
import { TrackClickDirective } from '../../directives/track-click.directive';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { AccordionPanelComponent, AccordionComponent as KBAccordionComponent } from '@knorr-bremse-portals/ngx-components/components/accordion';
import { PanelContentComponent } from '@knorr-bremse-portals/ngx-components/components/panel-content';
import { PanelHeaderComponent } from '@knorr-bremse-portals/ngx-components/components/panel-header';
import { CardComponent } from '@knorr-bremse-portals/ngx-components/components/card';
import { HtmlComponent } from '@knorr-bremse-portals/ngx-components/components/html';
import { FsButtonComponent } from '../../components/fs-button/fs-button.component';
import { DownloadListComponent, isDownloadListSection } from '../download-list/download-list.component';
import { CopyTextComponent, isCopyTextSection } from '../copy-text/copy-text.component';
import { isMediumSection, MediumComponent } from '../medium/medium.component';
import { DomTableComponent, isDomTableSection } from '../dom-table/dom-table.component';
import { ColumnWidth } from '@knorr-bremse-portals/ngx-components';
import { isVideoSection, PlainVideoComponent, PlainVideoSection } from '../../components/plain-video/plain-video.component';

interface LegacyAccordionItemAttributes extends InnerSectionAttributes {
  headline: string;
  content: string;
  medium?: PlainImageSection | PlainVideoSection | InteractiveImageSection;
  mediumPosition?: 'top' | 'bottom';
  expanded?: boolean;
}

interface AccordionItemAttributes extends InnerSectionAttributes {
  headline: string;
  sections: AnySection[];
  expanded?: boolean;
}

type AccordionItemEditorIds = ['headline', 'content', 'medium', 'mediumPosition'];

type AccordionItemSection = InnerSection<AccordionItemAttributes | LegacyAccordionItemAttributes, AccordionItemEditorIds, 'accordion-item'>;

type AccordionSection = Section<
  {
    items: AccordionItemSection[];
    numColumns: 4 | 8;
  } & SectionAttributes,
  [],
  'accordion'
>;

@Component({
  selector: 'app-accordion',
  styleUrls: ['./accordion.component.scss'],
  templateUrl: './accordion.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: true,
  imports: [
    CommonModule, EditorIdDirective,
    InteractiveImageComponent, PlainVideoComponent, PlainImageComponent,
    TrackClickDirective,
    KBAccordionComponent, AccordionPanelComponent, PanelContentComponent, PanelHeaderComponent, CardComponent, HtmlComponent,
    FsButtonComponent,
    MediumComponent, CopyTextComponent, DownloadListComponent, DomTableComponent,
  ],
})
export class AccordionComponent extends SectionComponent<AccordionSection> implements OnInit, AfterViewInit {

  @ViewChildren(AccordionPanelComponent) private panelElements!: QueryList<AccordionPanelComponent>;

  protected hostElement = inject<ElementRef<HTMLElement>>(ElementRef).nativeElement;

  private platformBrowser = isPlatformBrowser(inject(PLATFORM_ID));
  private route = inject(ActivatedRoute);
  private router = inject(Router);
  private destroyRef = inject(DestroyRef);

  public override ngAfterViewInit(): void {
    super.ngAfterViewInit();

    this.initialPanelExpand();
    this.handleQueryParams();
  }

  public override ngOnInit(): void {
    super.ngOnInit();

    const columnAddition = ColumnWidth.Full / this.attributes.numColumns;
    const validColumnsWidths = Object.values(ColumnWidth);

    for (const item of this.attributes.items) {
      if (this.isLegacyAccordionItemAttributes(item.attributes)) {
        continue;
      }
      for (const section of item.attributes.sections) {
        const targetWidth = section.attributes.numColumns * columnAddition;
        if (validColumnsWidths.includes(targetWidth)) {
          section.attributes.numColumns = targetWidth as NumColumns;
        } else {
          section.attributes.numColumns = ColumnWidth.Full;
        }
      }
    }
  }

  private initialPanelExpand(): void {
    const urlSelectedHeadline = this.route.snapshot.queryParamMap.get(this.anchor);
    if (urlSelectedHeadline) {
      const foundItem = this.attributes.items.find((item) => item.attributes.headline === urlSelectedHeadline);
      if (foundItem) {
        const index = this.attributes.items.indexOf(foundItem);
        this.panelElements.get(index)?.toggle();
        return;
      }
    }

    this.attributes.items.forEach((item, index) => {
      if (item.attributes.expanded === true) {
        this.panelElements.get(index)?.toggle();
      }
    });
  }

  private handleQueryParams(): void {
    const expandedItem$ = (this.panelElements.changes as Observable<QueryList<AccordionPanelComponent>>).pipe(
      startWith(this.panelElements),
      mergeMap((panels: QueryList<AccordionPanelComponent>) => {
        return merge(...panels.map((panel, index) =>
          panel.expand.pipe(mergeMap(() => of(index))),
        ));
      }),
      map(expandedPanelndex => this.attributes.items[expandedPanelndex]),
      takeUntilDestroyed(this.destroyRef),
    );

    expandedItem$.subscribe((expandedItem) => {
      this.router.navigate([], preventScroll({
        relativeTo: this.route,
        queryParams: {
          [this.anchor]: expandedItem.attributes.headline,
        },
        queryParamsHandling: 'merge',
        replaceUrl: true,
      }, this.platformBrowser));
    });
  }

  protected isPlainImageSection = isPlainImageSection;
  protected isVideoSection = isVideoSection;
  protected isInteractiveImageSection = isInteractiveImageSection;
  protected isDownloadList = isDownloadListSection;
  protected isCopyTextSection = isCopyTextSection;
  protected isDomTableSection = isDomTableSection;
  protected isMediumSection = isMediumSection;
  protected isLegacyAccordionItemAttributes = (attributes: AccordionItemAttributes | LegacyAccordionItemAttributes): attributes is LegacyAccordionItemAttributes => {
    return 'content' in attributes;
  }

}
